import React from 'react';
import { motion } from 'framer-motion';
import { SectionTag } from './SectionTag';
import { BONOS, CATALOG_BLOCKS, TOMOS } from './salesData';
import { HERO_IMAGE_URL } from '../utils/headMeta';

/**
 * Los dos tomos vuelven a aparecer a mitad de página, rodeados de fichas de
 * receta flotantes, para que se vea la cantidad real de material que se recibe.
 * Las tapas no se modifican: se usa el mismo mockup del hero.
 */
export function TomosSection() {
  return <section className="relative overflow-hidden bg-pistacho-pale py-14 sm:py-20" aria-labelledby="tomos-title" data-id="element-274" data-mp="xeqt78bfzs16">
      <div className="mx-auto w-full max-w-6xl px-5 sm:px-8" data-id="element-275" data-mp="16cj84bd94xo1">
        <div className="grid gap-10 lg:grid-cols-12 lg:items-center lg:gap-12" data-id="element-276" data-mp="16ff6g0t0hod0">
          {/* Composición: mockup + fichas de receta flotando alrededor. */}
          <div className="relative order-2 lg:order-1 lg:col-span-7" data-id="element-277" data-mp="2hrfz3qszxwy1">
            <div className="mockup-ground absolute bottom-3 left-1/2 h-10 w-[70%] -translate-x-1/2 blur-md" aria-hidden data-id="element-278" data-mp="2mqswh5hhumev" />
            <img src={HERO_IMAGE_URL} alt="Los dos tomos de 100 New York Cookies con la colección de cookies delante." loading="lazy" decoding="async" className="animate-breathe relative mx-auto max-h-[300px] w-full object-contain drop-shadow-[0_26px_44px_rgba(53,47,50,0.22)] sm:max-h-[420px]" data-id="element-279" data-mp="2qm4h752jazjy" />

            {/* Fichas de receta: reutilizan las categorías reales del catálogo.
                En mobile van en un riel debajo; desde sm flotan alrededor. */}
            <div className="mt-5 grid grid-cols-3 gap-2.5 sm:mt-0 sm:block" data-id="element-280" data-mp="1ywj9o8pkiapw">
              {CATALOG_BLOCKS.slice(0, 3).map((block, index) => {
              const place = ['sm:absolute sm:left-0 sm:top-4 sm:-rotate-6', 'sm:absolute sm:right-0 sm:top-[36%] sm:rotate-[7deg]', 'sm:absolute sm:bottom-0 sm:left-[12%] sm:rotate-3'][index];
              return <motion.div key={block.id} initial={{
                opacity: 0,
                y: 14
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true,
                amount: 0.4
              }} transition={{
                duration: 0.3,
                ease: [0.23, 1, 0.32, 1],
                delay: 0.08 + index * 0.06
              }} className={`${place} rounded-3xl bg-cream p-2 shadow-bakery sm:w-[9.5rem] sm:p-2.5 lg:w-[10.5rem]`} data-id="element-281" data-mp="1aalcb2xbxyrv" data-mpi={index}>
                    <img src={block.image} alt={block.alt} loading="lazy" decoding="async" className="aspect-[4/3] w-full rounded-2xl object-cover" data-id="element-282" data-mp="1kb5dk5kun3a3" />
                    <p className="mt-2 px-1 font-sans text-[0.48rem] font-bold uppercase tracking-editorial text-caramel sm:text-[0.56rem]" data-id="element-283" data-mp="2gs025e5atn4f">
                      {block.count} recetas
                    </p>
                    <p className="mb-1 mt-0.5 px-1 font-display text-[0.74rem] leading-tight text-charcoal sm:text-[0.95rem]" data-id="element-284" data-mp="epy0bdmfnyl">
                      {block.title}
                    </p>
                  </motion.div>;
            })}
            </div>
          </div>

          <div className="order-1 lg:order-2 lg:col-span-5" data-id="element-285" data-mp="3ijt1xsruptxf">
            <SectionTag data-id="element-286" data-mp="3ik1j3wv0blef">Los dos tomos</SectionTag>
            <h2 id="tomos-title" className="mt-4 font-display text-[1.95rem] leading-[1.06] text-charcoal sm:text-[2.5rem]" data-id="element-287" data-mp="3dl82vwoc5gak">
              Todo lo que recibís
            </h2>

            <ul className="mt-7" data-id="element-288" data-mp="3nhunqlgnw7cl">
              {TOMOS.map(tomo => <li key={tomo.id} className="border-t border-charcoal/10 py-4" data-id="element-289" data-mp="17rhw4uaqrun0">
                  <p className="font-sans text-[0.58rem] font-bold uppercase tracking-editorial text-gold-deep" data-id="element-290" data-mp="17tksdw2hmk06">
                    {tomo.label}
                  </p>
                  <p className="mt-1.5 font-display text-[1.05rem] leading-snug text-charcoal sm:text-[1.25rem]" data-id="element-291" data-mp="38iielwixbd0f">
                    {tomo.title}
                  </p>
                </li>)}
            </ul>
          </div>
        </div>

        {/* Los 5 bonos de regalo: portadas protagonistas. En mobile son un riel
            horizontal para no estirar la sección; desde sm, una fila de cinco. */}
        <div className="mt-12 border-t border-charcoal/10 pt-8" data-id="element-292" data-mp="3rdpbj3mb1fhp">
          <p className="font-sans text-[0.6rem] font-bold uppercase tracking-editorial text-caramel sm:text-[0.66rem]" data-id="element-293" data-mp="87e3zkyio4a">
            + 5 bonos digitales de regalo
          </p>

          {/* El riel se anima como bloque: animar cada tarjeta con whileInView
              dejaría invisibles las que arrancan fuera del viewport horizontal. */}
          <motion.ul initial={{
          opacity: 0,
          y: 12
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          amount: 0.2
        }} transition={{
          duration: 0.28,
          ease: [0.23, 1, 0.32, 1]
        }} className="no-scrollbar mt-3 flex snap-x snap-mandatory gap-3.5 overflow-x-auto pb-1 pt-2 sm:mt-5 sm:grid sm:grid-cols-5 sm:gap-5 sm:overflow-visible sm:pt-0" data-id="element-294" data-mp="dz2d8k5nlquo">
            {BONOS.map(bono => <li key={bono.id} className="w-[42%] shrink-0 snap-start sm:w-auto" data-id="element-295" data-mp="f572vcm6ic6k">
                <div className="relative" data-id="element-296" data-mp="1bwiun9865wdx">
                  <img src={bono.image} alt={`Portada del bono: ${bono.name}`} loading="lazy" decoding="async" className="aspect-[3/4] w-full rounded-2xl bg-cream object-contain p-1 shadow-bakery-sm" data-id="element-297" data-mp="16utj7sh1bgpb" />
                  <span className="absolute -top-2 left-2 rounded-full bg-[#F47A2A] px-2.5 py-1 font-sans text-[0.5rem] font-bold uppercase tracking-[0.12em] text-white shadow-bakery-sm sm:text-[0.54rem]" data-id="element-298" data-mp="1ulxskusk65e3">
                    Bono gratis
                  </span>
                </div>
                <p className="mt-2.5 font-sans text-[0.74rem] font-bold leading-snug text-charcoal sm:text-[0.78rem]" data-id="element-299" data-mp="3p1e3zyyelshl">
                  {bono.name}
                </p>
              </li>)}
          </motion.ul>
        </div>
      </div>
    </section>;
}